import React, {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  createContext,
} from 'react';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { Tooltip, TooltipGroup } from './Tooltip.jsx';
import { useGlide } from './useGlide.js';

// TabsSubtle, cut down to Base UI. One shared pill travels to the pressed
// item on the 36/28 ladder. A fainter pill previews the item under the
// pointer, born at the pressed item. Arrows move focus; Enter or Space
// presses. No active-label collapse, and no weight animation.

const ToggleGroupContext = createContext({
  size: 'default',
  value: [],
  hoveredIndex: null,
  setHoveredIndex: () => {},
  tooltips: false,
});

export function ToggleGroup({
  size = 'default',
  value: valueProp,
  defaultValue,
  onValueChange,
  className,
  children,
  tooltips: tooltipsProp,
  ...props
}) {
  const listRef = useRef(null);
  const mouseInside = useRef(false);
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? []);
  const value = valueProp !== undefined ? valueProp : uncontrolled;
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(null);
  const [rects, setRects] = useState([]);

  const entries = Children.toArray(children).filter(
    (child) => isValidElement(child) && typeof child.type !== 'string',
  );
  const tooltips = tooltipsProp ?? (
    entries.length > 1 && entries.every(
      (child) => Boolean(child.props.icon) && !hasLabel(child.props.children) && child.props['aria-label'],
    )
  );
  const selectedIndex = entries.findIndex((child) => value.includes(child.props.value));
  const geometryKey = `${size}:${entries.map(childKey).join('|')}`;

  const measure = useCallback(() => {
    const root = listRef.current;
    if (!root) return;
    const next = readRects(root);
    setRects((prev) => (sameRects(prev, next) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    const root = listRef.current;
    if (!root) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    root.querySelectorAll('[data-toggle-index]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [measure, geometryKey]);

  const selectedRect = selectedIndex >= 0 ? rects[selectedIndex] : null;
  const hoverRect = hoveredIndex != null ? rects[hoveredIndex] : null;
  const focusRect = focusedIndex != null ? rects[focusedIndex] : null;
  const hoveringOther = Boolean(
    selectedRect && hoverRect && hoveredIndex !== selectedIndex,
  );
  const selectionMoves = useGlide(boxOf(selectedRect) ? selectedIndex : null);
  const focusMoves = useGlide(boxOf(focusRect) ? focusedIndex : null);

  const indexed = Children.map(children, (child, index) => (
    isValidElement(child) && typeof child.type !== 'string'
      ? cloneElement(child, { index })
      : child
  ));

  const group = (
    <ToggleGroupPrimitive
      {...props}
      className={['edl-toggle-group', className].filter(Boolean).join(' ')}
      data-size={size}
      value={value}
      onValueChange={(next, details) => {
        if (valueProp === undefined) setUncontrolled(next);
        onValueChange?.(next, details);
      }}
      ref={listRef}
      onMouseEnter={() => { mouseInside.current = true; }}
      // React drops mouseenter on a disabled button, so the hover from the item
      // before it would stay. Native mouseover still bubbles here.
      onMouseOver={(event) => {
        if (event.target.closest?.('[data-toggle-index][data-disabled]')) setHoveredIndex(null);
      }}
      onMouseLeave={() => {
        mouseInside.current = false;
        setHoveredIndex(null);
      }}
      onFocus={(event) => {
        const item = event.target.closest?.('[data-toggle-index]');
        if (!item || !listRef.current?.contains(item)) return;
        const index = Number(item.dataset.toggleIndex);
        setHoveredIndex(index);
        setFocusedIndex(item.matches(':focus-visible') ? index : null);
      }}
      onBlur={(event) => {
        if (listRef.current?.contains(event.relatedTarget)) return;
        setFocusedIndex(null);
        if (mouseInside.current) return;
        setHoveredIndex(null);
      }}
    >
      <ToggleGroupContext.Provider value={{ size, value, hoveredIndex, setHoveredIndex, tooltips, disabled: Boolean(props.disabled) }}>
        {boxOf(selectedRect) && (
          <div
            className={['edl-toggle-group__selected', selectionMoves ? 'is-moving' : ''].filter(Boolean).join(' ')}
            style={place(selectedRect, hoveringOther ? 0.8 : 1)}
            aria-hidden="true"
          />
        )}
        <HoverPill
          from={selectedRect}
          to={hoverRect}
          active={hoveringOther}
          mouseInside={mouseInside}
        />
        {boxOf(focusRect) && (
          <div
            className={['edl-toggle-group__ring', focusMoves ? 'is-moving' : ''].filter(Boolean).join(' ')}
            style={place(focusRect, undefined, 2)}
            aria-hidden="true"
          />
        )}
        {indexed}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
  return tooltips ? <TooltipGroup>{group}</TooltipGroup> : group;
}

export function ToggleGroupItem({ icon: Icon, className, children, index = 0, value, disabled: disabledProp, ...props }) {
  const { size, value: pressedValues, hoveredIndex, setHoveredIndex, tooltips, disabled: groupDisabled } = useContext(ToggleGroupContext);
  const disabled = Boolean(disabledProp) || groupDisabled;
  const iconOnly = Boolean(Icon) && !hasLabel(children);
  // A disabled item never previews or reads as hovered; the pressed one still does.
  const active = (hoveredIndex === index && !disabled) || pressedValues.includes(value);
  const item = (
    <TogglePrimitive
      {...props}
      value={value}
      disabled={disabled}
      className={['edl-toggle-group__item', className].filter(Boolean).join(' ')}
      data-toggle-index={index}
      data-icon-only={iconOnly ? '' : undefined}
      data-active={active ? '' : undefined}
      onMouseEnter={() => setHoveredIndex(disabled ? null : index)}
    >
      {Icon ? <Icon size={size === 'compact' ? 14 : 16} strokeWidth={1.5} aria-hidden="true" /> : null}
      {hasLabel(children) ? children : null}
    </TogglePrimitive>
  );
  return tooltips ? <Tooltip content={props['aria-label']}>{item}</Tooltip> : item;
}

function HoverPill({ from, to, active, mouseInside }) {
  const [phase, setPhase] = useState('off');
  const [at, setAt] = useState(null);
  const [opacity, setOpacity] = useState(0);
  const alive = useRef(false);
  const phaseRef = useRef('off');
  const fromKey = rectKey(from);
  const toKey = rectKey(to);
  const go = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  useLayoutEffect(() => {
    if (active && from && to) {
      if (!alive.current) {
        alive.current = true;
        go('enter');
        setAt(from);
        setOpacity(0);
        const id = requestAnimationFrame(() => {
          go('on');
          setAt(to);
          setOpacity(0.4);
        });
        return () => cancelAnimationFrame(id);
      }
      go('on');
      setAt(to);
      setOpacity(0.4);
      return undefined;
    }
    if (alive.current) {
      alive.current = false;
      if (!mouseInside.current && from) {
        go('back');
        setAt(from);
        setOpacity(0);
      } else {
        go('fade');
        setOpacity(0);
      }
    }
    if (phaseRef.current === 'off') return undefined;
    const id = window.setTimeout(() => go('off'), 180);
    return () => window.clearTimeout(id);
  }, [active, from, to, fromKey, toKey, mouseInside]);

  if (phase === 'off' || !at) return null;
  return (
    <div
      className="edl-toggle-group__hover"
      data-phase={phase}
      style={place(at, opacity)}
      aria-hidden="true"
    />
  );
}

function boxOf(rect) {
  return rect && rect.width > 0 && rect.height > 0 ? rect : null;
}

function place(rect, opacity, outset = 0) {
  return {
    transform: `translate(${rect.left - outset}px, ${rect.top - outset}px)`,
    width: rect.width + outset * 2,
    height: rect.height + outset * 2,
    opacity,
  };
}

function readRects(root) {
  return [...root.querySelectorAll('[data-toggle-index]')]
    .sort((a, b) => Number(a.dataset.toggleIndex) - Number(b.dataset.toggleIndex))
    .map((el) => ({
      left: el.offsetLeft,
      top: el.offsetTop,
      width: el.offsetWidth,
      height: el.offsetHeight,
    }));
}

function sameRects(a, b) {
  return a.length === b.length && a.every((rect, i) => rectKey(rect) === rectKey(b[i]));
}

function rectKey(rect) {
  return rect ? `${rect.left},${rect.top},${rect.width},${rect.height}` : '';
}

function childKey(child) {
  const icon = child.props.icon;
  const name = icon?.displayName || icon?.name || '';
  return `${child.props.value}:${name}:${labelText(child.props.children)}`;
}

function hasLabel(children) {
  return labelText(children) !== '';
}

function labelText(children) {
  return Children.toArray(children)
    .filter((child) => child != null && child !== false)
    .join('');
}
